import Import from "assets/img/svg/navbar/import";
import Upload from "assets/img/svg/upload";
import ImportData from "components/ImportData";
import { getPendingImports } from "components/ImportData/utils";
import ModalBox from "components/Modal";
import { openEditMode } from "components/NewForm/utils";
import AuthPage from "components/shared/AuthPage";
import type { Action } from "components/shared/ButtonMenu";
import ElementList from "components/shared/ElementList";
import { EL_SORT_STORAGE_KEY, getPersistedFilterOrder } from "components/shared/ElementList/sortStorage";
import { LIST_SKELETON_COUNT } from "components/shared/TextSkeleton";
import DetailPanelScroll from "components/shared/DetailPanelScroll";
import TitleListItem from "components/shared/TitleListItem";
import Title from "components/Title";
import TitleFormContainer from "components/TitleFormContainer";
import BulkUpdatesContainer from "containers/BulkUpdatesContainer";
import { useCompanySettings } from "hooks/useCompanySettings";
import useEditModeLock from "hooks/useEditModeLock";
import useListItemPreview from "hooks/useListItemPreview";
import useSelectFirstRouteItem from "hooks/useSelectFirstRouteItem";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchBrands } from "store/brandStore";
import type { AppDispatch } from "store/store";
import { useAppSelector } from "store/store";
import { exportTitleList, fetchTitles, setTitlePreview } from "store/titleStore";
import { fetchTags } from "store/titleStore";
import type { TParamsPagination } from "store/types";

import { discardDraft } from "../../store/priceHistory/priceHistoryStore";
import NewTitleSimplifiedModal from "./NewTitleSimplifiedModal";
import type { ITitle } from "./types";

/** Must match each `orderOptions[].name` below (used for persisted-sort validation on first fetch). */
const TITLE_SORT_FIELD_NAMES = ["creationDate", "releaseDate", "name"] as const;

const Titles = () => {
    const [t] = useTranslation();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { titlesStatus, countTitles, titles, title } = useAppSelector((state) => state.titles);
    const tagTitleList = useAppSelector((state) => state.titles.tags);
    const { brands } = useAppSelector((state) => state.brands);
    const { jobs } = useAppSelector((state) => state.jobs);
    const { pendingImports } = useAppSelector((state) => state.dataImport);
    const isInitialLoad = titlesStatus === "idle";

    const routeTitleId = params.id ? Number(params.id) : null;
    const [currentTitle, setCurrentTitle] = useState<number>(routeTitleId ?? -1);
    const [modalType, setModalType] = useState("");
    const [editMode, setEditMode] = useState(false);

    const { getCompanySettings, companyInfos } = useCompanySettings();
    const usesBrands = getCompanySettings("uses_multiple_brands");
    const usesAgreementsOnly = getCompanySettings("uses_agreements_only");

    /**
     * Object that contains refs for each item within list.
     */
    const elementListRef = {};

    const orderOptions: { name: string; label: string }[] = [
        { name: "creationDate", label: t("Filters.CreationDate") },
        { name: "releaseDate", label: t("Filters.PublicationDate") },
        { name: "name", label: t("Filters.TitleName") },
    ];

    useSelectFirstRouteItem(titles, routeTitleId, "/titles", setCurrentTitle);

    const { selectItem: setTitle } = useListItemPreview({
        items: titles,
        routeId: routeTitleId,
        currentId: title?.id,
        pathPrefix: "/titles",
        enabled: titles.length > 0,
        onPreview: (selectedTitle) => {
            setCurrentTitle(selectedTitle.id);
            dispatch(setTitlePreview(selectedTitle));
        },
    });

    useEffect(() => {
        // when create new title by simlpified modal, set currentTitle when state is changing
        setCurrentTitle((title as ITitle)?.id);
    }, [title]);

    const handleExport = () => dispatch(exportTitleList());

    useEffect(() => {
        if (!isInitialLoad) return;
        const sortFilters = getPersistedFilterOrder(EL_SORT_STORAGE_KEY.titles, TITLE_SORT_FIELD_NAMES);
        dispatch(fetchTitles({ limit: 100, ...sortFilters }));
    }, [dispatch]);

    const popoverMenu = {
        Imports: [
            {
                label: t("ImportData.ImportTitle"),
                icon: <Upload />,
                action: () => setModalType("importTitles"),
            },
            {
                label: t("ImportData.ImportTerms"),
                icon: <Upload />,
                action: () => setModalType("importTerms"),
            },
            {
                label: t("BulkUpdates.BulkUpdate"),
                icon: <Upload />,
                action: () => setModalType("updates"),
            },
        ],
        Exports: [
            {
                label: t("ImportData.ExportTitle"),
                icon: <Import />,
                action: () => handleExport(),
            },
        ],
    };

    useEffect(() => {
        if (tagTitleList === null) dispatch(fetchTags());
    }, [tagTitleList, dispatch]);

    useEditModeLock(editMode);

    useEffect(() => {
        if (!editMode) dispatch(discardDraft());
    }, [dispatch, editMode]);

    useEffect(() => {
        if (usesBrands && !brands.length) dispatch(fetchBrands(true));
    }, [usesBrands, brands.length, dispatch]);

    /**
     * @param action create with the full form or sipmplified creation
     */
    const handleNewTitle = (action?: "create" | "simplified-creation") => {
        if (action === "simplified-creation") setModalType("simpleCreation");
        else navigate("/new-title");
    };

    const newTitleMenuActions: Action[] = [
        {
            name: t("NewTitle.CreateNewTitle"),
            action: () => handleNewTitle("create"),
        },
        {
            name: t("NewTitle.QuickTitleCreation"),
            action: () => handleNewTitle("simplified-creation"),
            subtitle: t("NewTitle.SimplifiedForm"),
        },
    ];

    const pendingJobs = getPendingImports({ jobs, fakeImports: pendingImports, type: ["title"] });

    return (
        <AuthPage userAuthenticated={true} pageName="titles">
            <ElementList
                elements={titles}
                currentElement={currentTitle}
                setCurrentElement={setTitle}
                elementsStatus={titlesStatus}
                elementCount={countTitles}
                elementData={{
                    label: t("Titles.Titles"),
                    actionLabel: t("NewTitle.NewTitle"),
                    action: () => navigate("/new-title"),
                }}
                elementSearchFunc={(options: TParamsPagination) => dispatch(fetchTitles({ ...options }))}
                popoverMenu={popoverMenu}
                editMode={editMode}
                tagList={tagTitleList}
                orderOptions={orderOptions}
                usesBrands={usesBrands}
                brands={brands}
                companyName={companyInfos?.name}
                showTags
                isInitialLoad={isInitialLoad}
                listSkeleton={Array.from({ length: LIST_SKELETON_COUNT }, (_, index) => (
                    <TitleListItem key={`title-list-skeleton-${index}`} index={index} isLoading />
                ))}
                hasMultipleActionBtn={usesAgreementsOnly}
                btnMenuActions={newTitleMenuActions}
                pendingJobs={pendingJobs}
                sortStorageKey={EL_SORT_STORAGE_KEY.titles}
                showHasTermFilter
            >
                <>
                    {[...titles].map((element, index) => (
                        <TitleListItem
                            element={element}
                            currentElementId={currentTitle}
                            index={index}
                            action={setTitle}
                            key={`title-list-item-${index}`}
                            disabled={editMode && element.id != currentTitle}
                            ref={(el: HTMLDivElement | null) => (elementListRef[element.id] = el)}
                        />
                    ))}
                </>
            </ElementList>

            <DetailPanelScroll className="title-detail-scroll">
                <Title
                    editTitle={editMode}
                    openEditTitle={(formStep: string | undefined) =>
                        openEditMode({
                            elementListRef: { ...elementListRef },
                            elementId: currentTitle.toString(),
                            setEditMode,
                            formStep,
                        })
                    }
                />
                {editMode && (
                    <TitleFormContainer
                        mode="edit-page"
                        editModeActive={editMode}
                        closeEditMode={() => setEditMode(false)}
                    />
                )}
            </DetailPanelScroll>

            <NewTitleSimplifiedModal open={modalType === "simpleCreation"} onClose={() => setModalType("")} />

            <ModalBox
                open={modalType === "importTitles"}
                dictionaryKey={"titles"}
                onClose={() => setModalType("")}
                mainTitle={t("ImportData.ImportTitle")}
            >
                <ImportData dictionaryKey={"titles"} close={() => setModalType("")} />
            </ModalBox>
            <ModalBox
                open={modalType === "importTerms"}
                dictionaryKey={"terms"}
                onClose={() => setModalType("")}
                mainTitle={t("ImportData.ImportTerms")}
            >
                <ImportData dictionaryKey={"terms"} close={() => setModalType("")} />
            </ModalBox>
            {modalType === "updates" && (
                <BulkUpdatesContainer
                    openModal={modalType === "updates"}
                    closeModal={() => setModalType("")}
                    from="titles"
                />
            )}
        </AuthPage>
    );
};

export default Titles;
